import { useEffect, useRef, useCallback } from "react";
import { Client } from "@stomp/stompjs";

export const useWSClient = ({
  stompAddress,
  onConnectionSuccess,
  receiveCallbackError,
}) => {
  const wsRef = useRef({
    client: null,
    subscriptions: new Map(), // topic -> { callback, subscription }
  });

  const connect = useCallback(() => {
    const client = new Client({
      brokerURL: stompAddress,
      connectHeaders: {
        login: "guest",
        passcode: "guest",
      },
      debug: () => {}, // disable debug logs

      // Built-in automatic reconnect
      reconnectDelay: 3000, // reconnect every 3 seconds
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,

      onConnect: () => {
        // Fire callback
        onConnectionSuccess?.();

        // Re-subscribe to all topics after reconnect
        wsRef.current.subscriptions.forEach((entry, topic) => {
          const subscription = client.subscribe(
            `/topic/${topic}`,
            entry.wrapped
          );
          entry.subscription = subscription;
        });
      },

      onStompError: (frame) => {
        console.error("STOMP error:", frame.headers["message"]);
        receiveCallbackError?.(frame.headers["message"]);
      },

      onWebSocketError: (event) => {
        console.error("WebSocket error:", event.message);
        receiveCallbackError?.("WebSocket connection error");
      },
    });

    wsRef.current.client = client;
    client.activate(); // Connect WebSocket + STOMP
  }, [stompAddress, onConnectionSuccess, receiveCallbackError]);

  const subscribe = useCallback((topic, callback) => {
    const client = wsRef.current.client;
    if (!client || !client.connected) {
      console.warn("Attempted to subscribe before STOMP connection is ready.");
      return;
    }

    // Wrap callback to allow shared logic
    const wrapped = (message) => {
      try {
        callback?.(message);
      } catch (err) {
        console.error("WS callback error:", err);
      }
    };

    const subscription = client.subscribe(`/topic/${topic}`, wrapped);

    wsRef.current.subscriptions.set(topic, {
      callback,
      wrapped,
      subscription,
    });
  }, []);

  const unsubscribe = useCallback((topic) => {
    const entry = wsRef.current.subscriptions.get(topic);
    if (entry) {
      try {
        entry.subscription?.unsubscribe();
      } catch (e) {
        console.warn("Unsubscribe error:", e.message);
      }
      wsRef.current.subscriptions.delete(topic);
    }
  }, []);

  useEffect(() => {
    connect();

    return () => {
      const client = wsRef.current.client;

      // Unsubscribe cleanly
      wsRef.current.subscriptions.forEach((entry) => {
        try {
          entry.subscription?.unsubscribe();
        } catch {}
      });
      wsRef.current.subscriptions.clear();

      // Disconnect and close
      if (client) {
        client.deactivate(); // Safe shutdown
      }
    };
  }, []);

  return {
    subscribe,
    unsubscribe,
  };
};
